// @ts-nocheck
import { createService } from "@/integration/factory/service-factory.ts";
import {
  ICreateTeamScheduleCheckinRequest,
  ICreateTeamScheduleRequest,
  ITeamSchedule,
  ITeamScheduleCheckin,
  ITeamSchedulePaginated,
  IUpdateTeamScheduleRequest,
} from "../interfaces/team-schedule.interface";

/**
 * Service for team schedule-related API calls
 */
export default function teamScheduleService() {
  // Create base services for team schedule endpoints
  const teamsService = createService("/teams");

  /**
   * Get team schedules with pagination
   * @param teamId The team ID
   * @param page Page number (default: 1)
   * @param limit Items per page (default: 10)
   * @returns Paginated team schedules
   */
  const getTeamSchedules = async (
    teamId: string,
    page: number = 1,
    limit: number = 10
  ): Promise<ITeamSchedulePaginated> => {
    try {
      const params: Record<string, any> = { 
        page, 
        limit
      };

      // Date filters removed as API doesn't support them

      return await teamsService.customGet<ITeamSchedulePaginated>(
        `/${teamId}/schedules`,
        { params }
      );
    } catch (error) {
      console.error("Error fetching team schedules:", error);
      // Return a more informative error object
      return {
        data: [],
        page,
        limit,
        total: 0,
        totalPage: 0,
        error: error instanceof Error ? error.message : "Unknown error occurred"
      } as ITeamSchedulePaginated & { error?: string };
    }
  };

  /**
   * Get a specific team schedule by ID
   * @param teamId The team ID
   * @param scheduleId The schedule ID
   * @returns The team schedule
   */
  const getTeamScheduleById = async (
    teamId: string,
    scheduleId: string
  ): Promise<ITeamSchedule | null> => {
    try {
      return await teamsService.customGet<ITeamSchedule>(
        `/${teamId}/schedules/${scheduleId}`
      );
    } catch (error) {
      console.error("Error fetching team schedule:", error);
      return null;
    }
  };

  /**
   * Create a team schedule
   * @param teamId The team ID
   * @param data The schedule data
   * @returns The created team schedule
   */
  const createTeamSchedule = async (
    teamId: string,
    data: ICreateTeamScheduleRequest
  ): Promise<ITeamSchedule | null> => {
    try {
      return await teamsService.customPost<ICreateTeamScheduleRequest>(
        `/${teamId}/schedules`,
        { ...data, team_id: teamId }
      );
    } catch (error) {
      console.error("Error creating team schedule:", error);
      return null;
    }
  };

  /**
   * Update a team schedule
   * @param teamId The team ID
   * @param scheduleId The schedule ID
   * @param data The schedule data
   * @returns The updated team schedule
   */
  const updateTeamSchedule = async (
    teamId: string,
    scheduleId: string,
    data: IUpdateTeamScheduleRequest
  ): Promise<ITeamSchedule | null> => {
    try {
      return await teamsService.customPut<ITeamSchedule>(
        `/${teamId}/schedules/${scheduleId}`,
        data
      );
    } catch (error) {
      console.error("Error updating team schedule:", error);
      return null;
    }
  };

  /**
   * Delete a team schedule
   * @param teamId The team ID
   * @param scheduleId The schedule ID
   * @returns Success status
   */
  const deleteTeamSchedule = async (
    teamId: string,
    scheduleId: string
  ): Promise<boolean> => {
    try {
      await teamsService.customDelete(
        `/${teamId}/schedules/${scheduleId}`
      );
      return true;
    } catch (error) {
      console.error("Error deleting team schedule:", error);
      return false;
    }
  };

  /**
   * Get check-ins for a schedule
   * @param teamId The team ID
   * @param scheduleId The schedule ID
   * @returns Array of check-ins
   */
  const getScheduleCheckins = async (
    teamId: string,
    scheduleId: string
  ): Promise<ITeamScheduleCheckin[]> => {
    try {
      return await teamsService.customGet<ITeamScheduleCheckin[]>(
        `/${teamId}/schedules/${scheduleId}/checkins`
      );
    } catch (error) {
      console.error("Error fetching schedule check-ins:", error);
      return [];
    }
  };

  /**
   * Create a check-in for a schedule
   * @param teamId The team ID
   * @param scheduleId The schedule ID
   * @param data The check-in data
   * @returns The created check-in
   */
  const createScheduleCheckin = async (
    teamId: string,
    scheduleId: string,
    data: ICreateTeamScheduleCheckinRequest
  ): Promise<ITeamScheduleCheckin | null> => {
    try {
      return await teamsService.customPost<ITeamScheduleCheckin>(
        `/${teamId}/schedules/${scheduleId}/checkins`,
        { ...data, schedule_id: scheduleId }
      );
    } catch (error) {
      console.error("Error creating schedule check-in:", error);
      return null;
    }
  };




  /**
   * Delete a check-in for a schedule
   * @param teamId The team ID
   * @param scheduleId The schedule ID
   * @param checkinId The check-in ID
   * @returns Success status
   */
  const deleteScheduleCheckin = async (
    teamId: string,
    scheduleId: string,
    checkinId: string
  ): Promise<boolean> => {
    try {
      await teamsService.customDelete(
        `/${teamId}/schedules/${scheduleId}/checkins/${checkinId}`
      );
      return true;
    } catch (error) {
      console.error("Error deleting schedule check-in:", error);
      return false;
    }
  };

  /**
   * Update a check-in confirmation status
   * @param teamId The team ID
   * @param scheduleId The schedule ID
   * @param checkinId The check-in ID
   * @param isConfirmed Whether the check-in is confirmed or not
   * @returns The updated check-in
   */
  const updateScheduleCheckinConfirmation = async (
    teamId: string,
    scheduleId: string,
    checkinId: string,
    isConfirmed: boolean
  ): Promise<ITeamScheduleCheckin | null> => {
    try {
      return await teamsService.customPut<ITeamScheduleCheckin>(
        `/${teamId}/schedules/${scheduleId}/checkins/${checkinId}/confirmation`,
        { is_confirmed: isConfirmed }
      );
    } catch (error) {
      console.error("Error updating schedule check-in confirmation:", error);
      return null;
    }
  };

  return {
    getTeamSchedules,
    getTeamScheduleById,
    createTeamSchedule,
    updateTeamSchedule,
    deleteTeamSchedule,
    getScheduleCheckins,
    createScheduleCheckin,
    deleteScheduleCheckin,
    updateScheduleCheckinConfirmation,
  };
}
